import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Loader2, FileText, Trash2, AlertCircle, Plus, TrendingUp, Activity } from 'lucide-react';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import { formatAmount } from './utils/formatters';

interface SavedDataset {
  id: string;
  name: string;
  created_at: string;
  site_id: number;
  total_transactions: number;
  total_volume: number;
  total_site_fees: number;
  total_bank_fees: number;
  total_reseller_fees: number;
}

export function SavedDatasets() {
  const [datasets, setDatasets] = useState<SavedDataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [datasetToDelete, setDatasetToDelete] = useState<SavedDataset | null>(null);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    try {
      const { data, error } = await supabase
        .from('datasets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDatasets(data || []);
    } catch (err) {
      console.error('Error loading datasets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!datasetToDelete) return;

    setDeleting(datasetToDelete.id);
    setDeleteError(null);

    try {
      const { error } = await supabase
        .from('datasets')
        .delete()
        .eq('id', datasetToDelete.id);

      if (error) throw error;
      
      setDatasets(datasets.filter(d => d.id !== datasetToDelete.id));
      setDatasetToDelete(null);
    } catch (err) {
      console.error('Error deleting dataset:', err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete dataset');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-[14px] text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (datasets.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <FileText className="w-7 h-7 text-slate-400" />
        </div>
        <h3 className="text-[15px] font-semibold text-slate-900">No datasets</h3>
        <p className="mt-1 text-[14px] text-slate-500">Get started by creating a new dataset.</p>
        <div className="mt-6">
          <Link to="/datasets/new" className="btn-primary">
            <Plus className="w-4 h-4" />
            Create new dataset
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {deleteError && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-[13px] text-red-600">{deleteError}</p>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Saved Datasets</h1>
          <p className="page-subtitle">Click on a dataset to view details</p>
        </div>
        <Link to="/datasets/new" className="btn-primary">
          <Plus className="w-4 h-4" />
          Create new dataset
        </Link>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {datasets.map((dataset) => {
          const netRevenue = dataset.total_site_fees - dataset.total_bank_fees - dataset.total_reseller_fees;
          return (
            <div key={dataset.id} className="card-hover group">
              <div className="p-5">
                <div className="flex justify-between items-start">
                  <Link to={`/datasets/${dataset.id}`} className="flex-1 min-w-0">
                    <h2 className="text-[15px] font-semibold text-slate-900 group-hover:text-violet-600 transition-colors truncate">
                      {dataset.name}
                    </h2>
                    <p className="mt-0.5 text-[12px] text-slate-400">
                      Site {dataset.site_id}
                    </p>
                  </Link>
                  <button
                    onClick={(e) => { e.preventDefault(); setDatasetToDelete(dataset); }}
                    disabled={deleting === dataset.id}
                    className="btn-ghost flex-shrink-0"
                  >
                    {deleting === dataset.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="stat-label">Transactions</p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">
                      {dataset.total_transactions.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="stat-label">Volume</p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">
                      {formatAmount(dataset.total_volume)}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-[13px]">
                  <div className="flex justify-between text-slate-500">
                    <span>Site Fees</span>
                    <span className="font-medium text-slate-700">{formatAmount(dataset.total_site_fees)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>Bank Fees</span>
                    <span className="font-medium text-slate-700">-{formatAmount(dataset.total_bank_fees)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>Reseller Fees</span>
                    <span className="font-medium text-slate-700">-{formatAmount(dataset.total_reseller_fees)}</span>
                  </div>
                  <div className="pt-1.5 mt-1.5 border-t border-slate-100 flex justify-between">
                    <span className="font-medium text-slate-700">Net Revenue</span>
                    <span className="font-semibold text-emerald-600">{formatAmount(netRevenue)}</span>
                  </div>
                </div>
              </div>
              
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-100">
                <span className="text-[12px] text-slate-400">
                  Created {new Date(dataset.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <DeleteConfirmationModal
        title="Delete Dataset"
        message={`Are you sure you want to delete "${datasetToDelete?.name}"? This action cannot be undone.`}
        isOpen={!!datasetToDelete}
        isDeleting={!!deleting}
        onConfirm={handleDelete}
        onCancel={() => setDatasetToDelete(null)}
      />
    </div>
  );
}
